/* eslint brace-style: ["error", "allman"] */

/**
 * A comprehension the processor can consume without holding it in memory.
 *
 * The processor used to take the whole comprehension as one object, which caps a load at whatever
 * fits in the heap — and the records are the overwhelming majority of that. Everything it does
 * before the records reach an adapter, though, needs only two things up front:
 *
 *   1. which entity types are present, and
 *   2. the *shape* of each — the field names, which is what the dependency sort reads.
 *
 * So a source declares those two, and hands records over in batches. A caller with a comprehension
 * already in memory builds one with `fromComprehension`; a caller reading a lake table builds one
 * that pages.
 *
 * ## Shapes carry two field sets, and the distinction is load-bearing
 *
 * The dependency sort treats the two reference forms differently, and collapsing them changes the
 * graph:
 *
 *   - `GUID<Entity>` / `_GUID<Entity>` create an edge only when some record holds a **truthy value**.
 *     A record carrying `GUIDMaterial: ''` does not depend on Material.
 *   - `…_Via_GUID<Entity>` creates an edge on **field name alone**.
 *
 * A shape built from field names only would invent dependencies from empty columns — and since the
 * sort throws on a cycle, an invented edge can fail a load that would otherwise have run.
 *
 * @typedef {Object} EntityShape
 * @property {Array<string>} Fields - Every field name seen on any record of this entity.
 * @property {Array<string>} PopulatedFields - Field names that held a truthy value on some record.
 *
 * @typedef {Object} ComprehensionSource
 * @property {Array<string>} EntityTypes - Entity names, in no particular order; the sort orders them.
 * @property {Object<string, EntityShape>} EntityShapes
 * @property {(pEntity: string) => number|null} recordCount - Known count, or null when unknowable
 *           without reading (a paging source may not know until it has read).
 * @property {(pEntity: string, fOnBatch: (pRecords: Array<Object>, fNext: (pError?: Error) => void) => void, fDone: (pError?: Error) => void) => void} streamRecords
 */

/**
 * Fold one record into an accumulating shape.
 *
 * @param {EntityShape} pShape
 * @param {Object} pRecord
 * @return {EntityShape}
 */
const foldRecordIntoShape = function(pShape, pRecord)
{
	if (!pRecord || (typeof(pRecord) !== 'object'))
	{
		return pShape;
	}
	for (const tmpFieldKey of Object.keys(pRecord))
	{
		if (pShape.Fields.indexOf(tmpFieldKey) < 0)
		{
			pShape.Fields.push(tmpFieldKey);
		}
		if (pRecord[tmpFieldKey] && (pShape.PopulatedFields.indexOf(tmpFieldKey) < 0))
		{
			pShape.PopulatedFields.push(tmpFieldKey);
		}
	}
	return pShape;
};

/**
 * @return {EntityShape}
 */
const emptyShape = function()
{
	return { Fields: [], PopulatedFields: [] };
};

/**
 * @param {Array<Object>|Object<string, Object>} pRecords
 * @return {EntityShape}
 */
const deriveEntityShape = function(pRecords)
{
	const tmpShape = emptyShape();
	const tmpList = Array.isArray(pRecords) ? pRecords : Object.values(pRecords || {});
	for (const tmpRecord of tmpList)
	{
		foldRecordIntoShape(tmpShape, tmpRecord);
	}
	return tmpShape;
};

/**
 * Wrap an in-memory comprehension as a source, so the streaming path is the only path the processor
 * has and the existing entry point keeps working unchanged.
 *
 * @param {Object<string, Array<Object>|Object<string, Object>>} pComprehension
 * @param {Object} [pOptions]
 * @param {number} [pOptions.BatchSize] - Records handed over per batch.
 * @return {ComprehensionSource}
 */
const fromComprehension = function(pComprehension, pOptions)
{
	const tmpComprehension = pComprehension || {};
	const tmpBatchSize = (pOptions && pOptions.BatchSize) || 5000;
	const tmpEntityTypes = Object.keys(tmpComprehension);
	/** @type {Object<string, EntityShape>} */
	const tmpShapes = {};
	for (const tmpEntity of tmpEntityTypes)
	{
		tmpShapes[tmpEntity] = deriveEntityShape(tmpComprehension[tmpEntity]);
	}

	const recordsOf = function(pEntity)
	{
		const tmpRecords = tmpComprehension[pEntity];
		if (!tmpRecords)
		{
			return [];
		}
		return Array.isArray(tmpRecords) ? tmpRecords : Object.values(tmpRecords);
	};

	return {
		EntityTypes: tmpEntityTypes,
		EntityShapes: tmpShapes,
		recordCount: (pEntity) => recordsOf(pEntity).length,
		streamRecords: (pEntity, fOnBatch, fDone) =>
		{
			const tmpRecords = recordsOf(pEntity);
			let tmpOffset = 0;
			const fPump = () =>
			{
				if (tmpOffset >= tmpRecords.length)
				{
					return fDone();
				}
				const tmpBatch = tmpRecords.slice(tmpOffset, tmpOffset + tmpBatchSize);
				tmpOffset += tmpBatchSize;
				// Recursing through the callback would grow the stack by one frame per batch on an
				// in-memory source, where nothing yields to the event loop between batches.
				return fOnBatch(tmpBatch, (pError) => (pError ? fDone(pError) : setImmediate(fPump)));
			};
			return fPump();
		},
	};
};

/**
 * A comprehension table stores either one record per row — `EntityName`, `RecordGUID`,
 * `RecordJSON` — or one CHUNK of records per row. The chunked layout exists because a single-pass
 * streaming clone cannot produce a per-record `RecordJSON`: `{~DataJson:Record~}` in that layout
 * serializes the source row, and building the JSON by string interpolation instead is what
 * produced 268 malformed rows the last time it was tried. A chunk's `RecordsJSON` is serialized
 * from the already-projected records, so the problem does not arise.
 *
 * @typedef {Object} ChunkRow
 * @property {string} EntityName
 * @property {number} ChunkOrdinal - 0-based, contiguous within an entity.
 * @property {number} ChunkCount - Chunks this entity is expected to have.
 * @property {number} RecordCount - Records in this chunk.
 * @property {number} SourceRowCount - Records this entity is expected to have.
 * @property {string} RecordsJSON - JSON array of records.
 */

/**
 * @param {Object} pRow
 * @return {boolean}
 */
const isChunkRow = function(pRow)
{
	return !!pRow && (typeof(pRow) === 'object') && Object.prototype.hasOwnProperty.call(pRow, 'RecordsJSON');
};

/**
 * @param {string|Array<Object>} pRecordsJSON
 * @param {string} pWhere - identifies the row in an error message
 * @return {Array<Object>}
 */
const parseChunkRecords = function(pRecordsJSON, pWhere)
{
	if (Array.isArray(pRecordsJSON))
	{
		return pRecordsJSON;
	}
	let tmpParsed;
	try
	{
		tmpParsed = JSON.parse(pRecordsJSON);
	}
	catch (pError)
	{
		throw new Error(`${pWhere}: RecordsJSON is not valid JSON — ${pError.message}`);
	}
	if (!Array.isArray(tmpParsed))
	{
		throw new Error(`${pWhere}: RecordsJSON parsed to ${typeof(tmpParsed)}, expected an array of records.`);
	}
	return tmpParsed;
};

/**
 * Everything a chunked entity claims about itself, accumulated as its rows are read.
 *
 * @return {Object}
 */
const emptyChunkTally = function()
{
	return { Ordinals: [], RecordSum: 0, DeclaredChunks: null, DeclaredRecords: null, Shape: emptyShape() };
};

/**
 * Decide whether an entity's chunks amount to a complete set.
 *
 * A dropped write costs `ChunkSize` records here rather than one, and `WriteRecords` has reported
 * success while losing whole chunks. So this is a precondition, not a diagnostic: three
 * independent invariants, any of which failing means the table is not loadable.
 *
 * @param {string} pEntity
 * @param {Object} pTally
 * @return {Array<string>} problems; empty when the entity is complete
 */
const findChunkProblems = function(pEntity, pTally)
{
	const tmpProblems = [];
	if (pTally.DeclaredChunks === null)
	{
		return [ `${pEntity}: no chunk rows found` ];
	}
	if (pTally.Ordinals.length !== pTally.DeclaredChunks)
	{
		tmpProblems.push(`${pEntity}: ${pTally.Ordinals.length} chunk row(s) present, ${pTally.DeclaredChunks} declared`);
	}
	const tmpSorted = pTally.Ordinals.slice().sort((pLeft, pRight) => pLeft - pRight);
	for (let i = 0; i < tmpSorted.length; i++)
	{
		if (tmpSorted[i] !== i)
		{
			tmpProblems.push(`${pEntity}: chunk ordinals are not contiguous — expected ${i}, found ${tmpSorted[i]}`);
			break;
		}
	}
	if (pTally.RecordSum !== pTally.DeclaredRecords)
	{
		tmpProblems.push(`${pEntity}: chunk record counts sum to ${pTally.RecordSum}, SourceRowCount declares ${pTally.DeclaredRecords}`);
	}
	return tmpProblems;
};

/**
 * Build a source over a chunked comprehension table.
 *
 * Two passes. The first reads every chunk row to derive each entity's shape and prove the set
 * complete, keeping only the shape — the records are parsed and discarded, so memory stays at one
 * chunk. The second pass is `streamRecords`, which re-reads and hands each chunk over as a batch.
 *
 * The shape cannot be sampled: `PopulatedFields` drives the dependency sort, and a populated field
 * that appears only in a late record would go unseen, dropping an edge and letting a child load
 * before its parent.
 *
 * @param {(pEntity: string|null, pOffset: number, pLimit: number, fCallback: (pError: Error|null, pRows?: Array<ChunkRow>) => void) => void} fReadChunkPage
 *        Pages chunk rows in a stable order. `pEntity` null means every entity.
 * @param {Object} [pOptions]
 * @param {number} [pOptions.PageSize] - Chunk ROWS per read. Resident records are this times the
 *        chunk size, so it is deliberately small.
 * @param {function} fCallback - function(pError, pSource)
 * @return {void}
 */
const fromChunkedTable = function(fReadChunkPage, pOptions, fCallback)
{
	const tmpPageSize = (pOptions && pOptions.PageSize) || 20;
	/** @type {Object<string, Object>} */
	const tmpTallies = {};
	let tmpOffset = 0;

	const fScanPage = () =>
	{
		fReadChunkPage(null, tmpOffset, tmpPageSize, (pError, pRows) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}
			const tmpRows = Array.isArray(pRows) ? pRows : [];
			if (tmpRows.length === 0)
			{
				return fFinishScan();
			}
			for (const tmpRow of tmpRows)
			{
				const tmpEntity = tmpRow.EntityName;
				if (!tmpEntity)
				{
					return fCallback(new Error(`Chunk row at offset ${tmpOffset} is missing EntityName — not a comprehension table.`));
				}
				if (!tmpTallies[tmpEntity])
				{
					tmpTallies[tmpEntity] = emptyChunkTally();
				}
				const tmpTally = tmpTallies[tmpEntity];
				let tmpRecords;
				try
				{
					tmpRecords = parseChunkRecords(tmpRow.RecordsJSON, `${tmpEntity} chunk ${tmpRow.ChunkOrdinal}`);
				}
				catch (pParseError)
				{
					return fCallback(pParseError);
				}
				tmpTally.Ordinals.push(parseInt(tmpRow.ChunkOrdinal, 10));
				tmpTally.RecordSum += tmpRecords.length;
				tmpTally.DeclaredChunks = parseInt(tmpRow.ChunkCount, 10);
				tmpTally.DeclaredRecords = parseInt(tmpRow.SourceRowCount, 10);
				// RecordCount is what the row claims; the parsed length is what it holds. A
				// disagreement means the row was truncated in transit and the sum below would
				// otherwise validate against the claim rather than the contents.
				const tmpClaimed = parseInt(tmpRow.RecordCount, 10);
				if (Number.isFinite(tmpClaimed) && (tmpClaimed !== tmpRecords.length))
				{
					return fCallback(new Error(`${tmpEntity} chunk ${tmpRow.ChunkOrdinal}: RecordCount claims ${tmpClaimed} but RecordsJSON holds ${tmpRecords.length}.`));
				}
				for (const tmpRecord of tmpRecords)
				{
					foldRecordIntoShape(tmpTally.Shape, tmpRecord);
				}
			}
			tmpOffset += tmpRows.length;
			return setImmediate(fScanPage);
		});
	};

	const fFinishScan = () =>
	{
		const tmpEntityTypes = Object.keys(tmpTallies);
		if (tmpEntityTypes.length === 0)
		{
			return fCallback(new Error('The chunked comprehension table holds no rows.'));
		}
		let tmpProblems = [];
		for (const tmpEntity of tmpEntityTypes)
		{
			tmpProblems = tmpProblems.concat(findChunkProblems(tmpEntity, tmpTallies[tmpEntity]));
		}
		if (tmpProblems.length > 0)
		{
			return fCallback(new Error(`The chunked comprehension is incomplete and was not loaded: ${tmpProblems.join('; ')}.`));
		}

		/** @type {Object<string, EntityShape>} */
		const tmpShapes = {};
		for (const tmpEntity of tmpEntityTypes)
		{
			tmpShapes[tmpEntity] = tmpTallies[tmpEntity].Shape;
		}

		return fCallback(null,
			{
				EntityTypes: tmpEntityTypes,
				EntityShapes: tmpShapes,
				recordCount: (pEntity) => (tmpTallies[pEntity] ? tmpTallies[pEntity].RecordSum : 0),
				streamRecords: (pEntity, fOnBatch, fDone) =>
				{
					let tmpStreamOffset = 0;
					const fPump = () =>
					{
						fReadChunkPage(pEntity, tmpStreamOffset, tmpPageSize, (pError, pRows) =>
						{
							if (pError)
							{
								return fDone(pError);
							}
							const tmpRows = Array.isArray(pRows) ? pRows : [];
							if (tmpRows.length === 0)
							{
								return fDone();
							}
							tmpStreamOffset += tmpRows.length;
							let tmpIndex = 0;
							const fNextRow = () =>
							{
								if (tmpIndex >= tmpRows.length)
								{
									return setImmediate(fPump);
								}
								const tmpRow = tmpRows[tmpIndex++];
								let tmpRecords;
								try
								{
									tmpRecords = parseChunkRecords(tmpRow.RecordsJSON, `${pEntity} chunk ${tmpRow.ChunkOrdinal}`);
								}
								catch (pParseError)
								{
									return fDone(pParseError);
								}
								// One chunk is one batch — the shape was built for exactly this.
								return fOnBatch(tmpRecords, (pBatchError) => (pBatchError ? fDone(pBatchError) : setImmediate(fNextRow)));
							};
							return fNextRow();
						});
					};
					return fPump();
				},
			});
	};

	return fScanPage();
};

/**
 * @param {any} pCandidate
 * @return {boolean} True when this is already a source rather than a raw comprehension.
 */
const isComprehensionSource = function(pCandidate)
{
	return !!pCandidate && Array.isArray(pCandidate.EntityTypes) && (typeof(pCandidate.streamRecords) === 'function');
};

/**
 * Accept either shape at an entry point, so callers holding a comprehension keep working.
 *
 * @param {Object|ComprehensionSource} pCandidate
 * @return {ComprehensionSource}
 */
const asComprehensionSource = function(pCandidate)
{
	return isComprehensionSource(pCandidate) ? pCandidate : fromComprehension(pCandidate);
};

module.exports =
	{
		deriveEntityShape,
		foldRecordIntoShape,
		emptyShape,
		fromComprehension,
		fromChunkedTable,
		isChunkRow,
		parseChunkRecords,
		findChunkProblems,
		isComprehensionSource,
		asComprehensionSource,
	};
